import React, { useState, useEffect } from 'react';
import axios from "axios";
import config from '../../Common/Configurations/APIConfig';
import AdminHeader from '../../Common/Layout/AdminHeader';
import { rexTechTheme } from '../../Common/Theme';
import { 
  Users,
  Search,
  Filter,
  Mail,
  Phone,
  Building,
  CheckCircle,
  XCircle,
  Plus,
  Globe,
  MapPin,
  DollarSign,
  Briefcase,
  Upload,
  AlertCircle,
  Save,
  X,
  Eye,
  EyeOff
} from 'lucide-react';

const Clients = () => {
    const [clients, setClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        inactive: 0
    });
    
    // Company creation state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    
    // Company form data
    const [companyForm, setCompanyForm] = useState({
        name: '',
        companyName: '',
        email: '',
        phoneNumber: '',
        address: '',
        city: '',
        country: '',
      currency: 'PLN',
            companyType: 'CLIENT',
            password: 'Test@123',
            userType: 'CLIENTUSER',
        isActive: true
    });
    
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        GetData();
    }, []);

    useEffect(() => {
        const filtered = clients.filter(client =>
            client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            client.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            client.phoneNumber?.includes(searchTerm) ||
            client.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            client.branchName?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredClients(filtered);
    }, [searchTerm, clients]);

    // ================= API Calls =================

    const GetData = async () => {
        try {
            setIsLoading(true);
            // Call the GetByRole API with roleName = "CLIENT"
            const res = await axios.get(`${config.APIACTIVEURL}${config.GETALLCOMPANIES}?companyType=${'CLIENT'}`);
            const data = res.data|| [];
            setClients(data);
            setFilteredClients(data);
            
            // Calculate stats
            const activeCount = data.filter(c => c.isActive).length;
            const inactiveCount = data.filter(c => !c.isActive).length;
            setStats({
                total: data.length,
                active: activeCount,
                inactive: inactiveCount
            });
        } catch (error) {
            console.error("Error fetching clients", error);
            showToast('error', 'Failed to load clients');
        } finally {
            setIsLoading(false);
        }
    };

    const createCompany = async (e) => {
        e.preventDefault();
        
        // Validate form
        const errors = validateCompanyForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        
        setIsCreating(true);
        setErrorMessage('');
        setSuccessMessage('');
        
        try {
            const formData = new FormData();
            
            // Append all form fields
            Object.keys(companyForm).forEach(key => {
                formData.append(key, companyForm[key]);
            });
            
            // Append logo file if exists
            if (logoFile) {
                formData.append('logoFile', logoFile);
            }
            
            const response = await axios.post(
                `${config.APIACTIVEURL}Company/Create`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                }
            );
            
            if (response.data.success) {
                setSuccessMessage('Company created successfully!');
                resetCompanyForm();
                setShowCreateModal(false);
                GetData(); // Refresh client list
                
                setTimeout(() => {
                    setSuccessMessage('');
                }, 3000);
            } else {
                setErrorMessage(response.data.message || 'Failed to create company');
            }
        } catch (error) {
            console.error("Error creating company:", error);
            if (error.response?.data?.message) {
                setErrorMessage(error.response.data.message);
            } else if (error.response?.status === 400) {
                setErrorMessage('Email already exists or invalid data');
            } else {
                setErrorMessage('Failed to create company. Please try again.');
            }
        } finally {
            setIsCreating(false);
        }
    };

    const validateCompanyForm = () => {
        const errors = {};
      
        
        if (!companyForm.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyForm.email)) {
            errors.email = 'Please enter a valid email address';
        }
        
        if (!companyForm.phoneNumber.trim()) {
            errors.phoneNumber = 'Phone number is required';
        }
        
        if (!companyForm.address.trim()) {
            errors.address = 'Address is required';
        }
        
        if (!companyForm.city.trim()) {
            errors.city = 'City is required';
        }
        
        if (!companyForm.country.trim()) {
            errors.country = 'Country is required';
        }
       
        
      
        return errors;
    };

    const handleCompanyInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setCompanyForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Clear error for this field
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
        
        setErrorMessage('');
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const resetCompanyForm = () => {
        setCompanyForm({
            name: '',
            companyName: '',
            email: '',
            phoneNumber: '',
            address: '',
            city: '',
            country: '',
            currency: 'PLN',
            companyType: 'CLIENT',
            password: 'Test@123',
            userType: 'CLIENTUSER',
            isActive: true
        });
        setFormErrors({});
        setLogoPreview(null);
        setLogoFile(null);
        setErrorMessage('');
        setSuccessMessage('');
    };

    const openCreateModal = () => {
        resetCompanyForm();
        setShowCreateModal(true);
    };

    const showToast = (type, message) => {
        // Implement toast notification here
        console.log(`${type}: ${message}`);
    };

    // ================= UI Components =================

    const LoadingSpinner = () => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: rexTechTheme.spacing.xxl,
            color: rexTechTheme.colors.primary.main
        }}>
            <div style={{
                width: '40px',
                height: '40px',
                border: `3px solid ${rexTechTheme.colors.primary.lightest}`,
                borderTop: `3px solid ${rexTechTheme.colors.primary.main}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: rexTechTheme.spacing.md
            }} />
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
            <span style={{ color: rexTechTheme.colors.neutral.darkGray }}>
                Loading clients...
            </span>
        </div>
    );

    const EmptyState = () => (
        <div style={{
            textAlign: 'center',
            padding: rexTechTheme.spacing.xxl,
            color: rexTechTheme.colors.neutral.darkGray
        }}>
            <Users size={48} style={{ 
                marginBottom: rexTechTheme.spacing.md,
                color: rexTechTheme.colors.neutral.gray,
                opacity: 0.5
            }} />
            <h3 style={{ 
                color: rexTechTheme.colors.neutral.darkGray,
                marginBottom: rexTechTheme.spacing.sm
            }}>
                No Clients Found
            </h3>
            <p style={{ marginBottom: rexTechTheme.spacing.lg }}>
                There are no clients registered in the system
            </p>
            <button
                onClick={openCreateModal}
                style={{
                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                    background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                    color: rexTechTheme.colors.neutral.white,
                    border: 'none',
                    borderRadius: rexTechTheme.borderRadius.medium,
                    fontSize: rexTechTheme.typography.fontSize.regular,
                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: rexTechTheme.spacing.sm,
                    margin: '0 auto',
                    boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                    ':hover': {
                        background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                    }
                }}
            >
                <Plus size={18} />
                Create First Client
            </button>
        </div>
    );

    const StatsCard = ({ title, value, icon: Icon, color, bgColor }) => (
        <div style={{
            backgroundColor: rexTechTheme.colors.neutral.white,
            borderRadius: rexTechTheme.borderRadius.large,
            padding: rexTechTheme.spacing.lg,
            boxShadow: rexTechTheme.shadows.light,
            border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
            flex: 1,
            minWidth: '200px'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
            }}>
                <div>
                    <div style={{
                        color: rexTechTheme.colors.neutral.darkGray,
                        fontSize: rexTechTheme.typography.fontSize.small,
                        marginBottom: rexTechTheme.spacing.xs
                    }}>
                        {title}
                    </div>
                    <div style={{
                        color: rexTechTheme.colors.primary.dark,
                        fontSize: rexTechTheme.typography.fontSize.xlarge,
                        fontWeight: rexTechTheme.typography.fontWeight.bold
                    }}>
                        {value}
                    </div>
                </div>
                <div style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: bgColor,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Icon size={20} color={color} />
                </div>
            </div>
        </div>
    );

    // Company Types for dropdown
    const companyTypes = [
        { value: 'SERVICE', label: 'Service Provider' },
        { value: 'RETAIL', label: 'Retail' },
        { value: 'MANUFACTURING', label: 'Manufacturing' },
        { value: 'IT', label: 'IT Services' },
        { value: 'CONSULTING', label: 'Consulting' },
        { value: 'OTHER', label: 'Other' }
    ];

    // Currencies for dropdown
    const currencies = [
        { value: 'USD', label: 'US Dollar ($)' },
        { value: 'EUR', label: 'Euro (€)' },
        { value: 'GBP', label: 'British Pound (£)' },
        { value: 'INR', label: 'Indian Rupee (₹)' },
        { value: 'AED', label: 'UAE Dirham (د.إ)' },
        { value: 'SAR', label: 'Saudi Riyal (﷼)' }
    ];

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: rexTechTheme.colors.neutral.lightGray,
            fontFamily: rexTechTheme.typography.fontFamily
        }}>
            <AdminHeader />
            
            <div style={{
                maxWidth: '1400px',
                margin: '0 auto',
                padding: rexTechTheme.spacing.xl
            }}>
                {/* Header Section */}
                <div style={{
                    marginBottom: rexTechTheme.spacing.xl
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: rexTechTheme.spacing.lg
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: rexTechTheme.spacing.md }}>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                backgroundColor: rexTechTheme.colors.primary.lightest,
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Users size={30} color={rexTechTheme.colors.primary.main} />
                            </div>
                            <div>
                                <h1 style={{
                                    fontSize: rexTechTheme.typography.fontSize.xxlarge,
                                    color: rexTechTheme.colors.primary.dark,
                                    fontWeight: rexTechTheme.typography.fontWeight.bold,
                                    marginBottom: rexTechTheme.spacing.xs
                                }}>
                                    Clients
                                </h1>
                                <p style={{
                                    color: rexTechTheme.colors.neutral.darkGray,
                                    fontSize: rexTechTheme.typography.fontSize.small
                                }}>
                                    View and manage all registered clients
                                </p>
                            </div>
                        </div>
                        
                        <button
                            onClick={openCreateModal}
                            style={{
                                padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                                color: rexTechTheme.colors.neutral.white,
                                border: 'none',
                                borderRadius: rexTechTheme.borderRadius.medium,
                                fontSize: rexTechTheme.typography.fontSize.regular,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: rexTechTheme.spacing.sm,
                                boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                                ':hover': {
                                    background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                                }
                            }}
                        >
                            <Plus size={18} />
                            Add New Client
                        </button>
                    </div>

                    {/* Stats Cards */}
                    <div style={{
                        display: 'flex',
                        gap: rexTechTheme.spacing.lg,
                        marginBottom: rexTechTheme.spacing.xl,
                        flexWrap: 'wrap'
                    }}>
                        <StatsCard
                            title="Total Clients"
                            value={stats.total}
                            icon={Users}
                            color={rexTechTheme.colors.primary.main}
                            bgColor={rexTechTheme.colors.primary.lightest}
                        />
                        <StatsCard
                            title="Active Clients"
                            value={stats.active}
                            icon={CheckCircle}
                            color={rexTechTheme.colors.primary.main}
                            bgColor={rexTechTheme.colors.primary.lightest}
                        />
                        <StatsCard
                            title="Inactive Clients"
                            value={stats.inactive}
                            icon={XCircle}
                            color={rexTechTheme.colors.accent.orange}
                            bgColor="#fef5e7"
                        />
                    </div>

                    {/* Search and Filter */}
                    <div style={{
                        backgroundColor: rexTechTheme.colors.neutral.white,
                        borderRadius: rexTechTheme.borderRadius.large,
                        padding: rexTechTheme.spacing.lg,
                        boxShadow: rexTechTheme.shadows.light,
                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`
                    }}>
                        <div style={{
                            display: 'flex',
                            gap: rexTechTheme.spacing.md,
                            alignItems: 'center'
                        }}>
                            <div style={{ position: 'relative', flex: 1 }}>
                                <Search 
                                    size={20} 
                                    style={{
                                        position: 'absolute',
                                        left: rexTechTheme.spacing.md,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: rexTechTheme.colors.neutral.darkGray
                                    }}
                                />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search clients by name or phone"
                                    style={{
                                        width: '100%',
                                        padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                        paddingLeft: `calc(${rexTechTheme.spacing.xl} + 20px)`,
                                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                        borderRadius: rexTechTheme.borderRadius.medium,
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        transition: 'all 0.2s ease',
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = rexTechTheme.colors.primary.main;
                                        e.target.style.boxShadow = `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`;
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = rexTechTheme.colors.neutral.gray;
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />
                            </div>
                            
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: rexTechTheme.spacing.sm,
                                color: rexTechTheme.colors.neutral.darkGray,
                                fontSize: rexTechTheme.typography.fontSize.small
                            }}>
                                <Filter size={16} />
                                <span>{filteredClients.length} clients found</span>
                            </div>
                        </div>
                        
                        {/* Success/Error Messages */}
                        {successMessage && (
                            <div style={{
                                marginTop: rexTechTheme.spacing.md,
                                padding: rexTechTheme.spacing.sm,
                                backgroundColor: rexTechTheme.colors.primary.lightest,
                                border: `1px solid ${rexTechTheme.colors.primary.light}`,
                                borderRadius: rexTechTheme.borderRadius.medium,
                                color: rexTechTheme.colors.primary.dark,
                                display: 'flex',
                                alignItems: 'center',
                                gap: rexTechTheme.spacing.sm,
                                fontSize: rexTechTheme.typography.fontSize.small
                            }}>
                                <CheckCircle size={16} />
                                {successMessage}
                            </div>
                        )}
                        
                        {errorMessage && (
                            <div style={{
                                marginTop: rexTechTheme.spacing.md,
                                padding: rexTechTheme.spacing.sm,
                                backgroundColor: rexTechTheme.colors.accent.lightest,
                                border: `1px solid ${rexTechTheme.colors.accent.orange}`,
                                borderRadius: rexTechTheme.borderRadius.medium,
                                color: rexTechTheme.colors.accent.orange,
                                display: 'flex',
                                alignItems: 'center',
                                gap: rexTechTheme.spacing.sm,
                                fontSize: rexTechTheme.typography.fontSize.small
                            }}>
                                <AlertCircle size={16} />
                                {errorMessage}
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Content */}
                <div style={{
                    backgroundColor: rexTechTheme.colors.neutral.white,
                    borderRadius: rexTechTheme.borderRadius.large,
                    boxShadow: rexTechTheme.shadows.light,
                    border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    overflow: 'hidden'
                }}>
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : filteredClients.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                minWidth: '1000px'
                            }}>
                                <thead>
                                    <tr style={{
                                        backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                        borderBottom: `2px solid ${rexTechTheme.colors.primary.main}`
                                    }}>
                                        <th style={{
                                            padding: rexTechTheme.spacing.lg,
                                            textAlign: 'left',
                                            fontWeight: rexTechTheme.typography.fontWeight.bold,
                                            color: rexTechTheme.colors.primary.dark,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Client
                                        </th>
                                        <th style={{
                                            padding: rexTechTheme.spacing.lg,
                                            textAlign: 'left',
                                            fontWeight: rexTechTheme.typography.fontWeight.bold,
                                            color: rexTechTheme.colors.primary.dark,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Contact Information
                                        </th>
                                        <th style={{
                                            padding: rexTechTheme.spacing.lg,
                                            textAlign: 'left',
                                            fontWeight: rexTechTheme.typography.fontWeight.bold,
                                            color: rexTechTheme.colors.primary.dark,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Branch
                                        </th>
                                        <th style={{
                                            padding: rexTechTheme.spacing.lg,
                                            textAlign: 'left',
                                            fontWeight: rexTechTheme.typography.fontWeight.bold,
                                            color: rexTechTheme.colors.primary.dark,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredClients.map((client, index) => (
                                        <tr 
                                            key={client.userId}
                                            style={{
                                                backgroundColor: index % 2 === 0 
                                                    ? rexTechTheme.colors.neutral.white 
                                                    : rexTechTheme.colors.neutral.lightGray,
                                                borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                                transition: 'all 0.2s ease',
                                                cursor: 'pointer'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = rexTechTheme.colors.primary.lightest;
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = index % 2 === 0 
                                                    ? rexTechTheme.colors.neutral.white 
                                                    : rexTechTheme.colors.neutral.lightGray;
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                        >
                                            <td style={{
                                                padding: rexTechTheme.spacing.lg
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: rexTechTheme.spacing.md
                                                }}>
                                                    <div style={{
                                                        width: '50px',
                                                        height: '50px',
                                                        backgroundColor: rexTechTheme.colors.primary.lightest,
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}>
                                                        <Users size={22} color={rexTechTheme.colors.primary.main} />
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{
                                                            color: rexTechTheme.colors.primary.dark,
                                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                                            fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                            marginBottom: rexTechTheme.spacing.xs
                                                        }}>
                                                            {client.name}
                                                        </div>
                                                       
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{
                                                padding: rexTechTheme.spacing.lg
                                            }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: rexTechTheme.spacing.sm }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: rexTechTheme.spacing.sm }}>
                                                        <Mail size={16} color={rexTechTheme.colors.neutral.darkGray} />
                                                        <a 
                                                            href={`mailto:${client.email}`}
                                                            style={{
                                                                color: rexTechTheme.colors.primary.main,
                                                                textDecoration: 'none',
                                                                fontSize: rexTechTheme.typography.fontSize.small
                                                            }}
                                                            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                                            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                                                        >
                                                            {client.email}
                                                        </a>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: rexTechTheme.spacing.sm }}>
                                                        <Phone size={16} color={rexTechTheme.colors.neutral.darkGray} />
                                                        <a 
                                                            href={`tel:${client.phoneNumber}`}
                                                            style={{
                                                                color: rexTechTheme.colors.primary.main,
                                                                textDecoration: 'none',
                                                                fontSize: rexTechTheme.typography.fontSize.small
                                                            }}
                                                            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                                            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                                                        >
                                                            {client.phoneNumber}
                                                        </a>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{
                                                padding: rexTechTheme.spacing.lg
                                            }}>
                                                <div style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: rexTechTheme.spacing.sm,
                                                    padding: rexTechTheme.spacing.sm,
                                                    backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                                    borderRadius: rexTechTheme.borderRadius.small,
                                                    maxWidth: '200px'
                                                }}>
                                                    <Building size={16} color={rexTechTheme.colors.primary.main} />
                                                    <span style={{ 
                                                        fontSize: rexTechTheme.typography.fontSize.small,
                                                        color: rexTechTheme.colors.primary.dark,
                                                        fontWeight: rexTechTheme.typography.fontWeight.medium
                                                    }}>
                                                        {client.branchName || 'MAIN'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td style={{
                                                padding: rexTechTheme.spacing.lg
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: rexTechTheme.spacing.sm
                                                }}>
                                                    <div style={{
                                                        width: '10px',
                                                        height: '10px',
                                                        backgroundColor: client.isActive 
                                                            ? rexTechTheme.colors.primary.main 
                                                            : rexTechTheme.colors.accent.orange,
                                                        borderRadius: '50%'
                                                    }} />
                                                    <span style={{
                                                        padding: `${rexTechTheme.spacing.xs} ${rexTechTheme.spacing.md}`,
                                                        backgroundColor: client.isActive 
                                                            ? rexTechTheme.colors.primary.lightest 
                                                            : '#fef5e7',
                                                        color: client.isActive 
                                                            ? rexTechTheme.colors.primary.dark 
                                                            : rexTechTheme.colors.accent.orange,
                                                        borderRadius: rexTechTheme.borderRadius.small,
                                                        fontSize: rexTechTheme.typography.fontSize.xs,
                                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                        border: `1px solid ${client.isActive 
                                                            ? rexTechTheme.colors.primary.light 
                                                            : '#f8c471'}`
                                                    }}>
                                                        {client.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Create Company Modal */}
                {showCreateModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: rexTechTheme.spacing.lg
                    }}>
                        <div style={{
                            backgroundColor: rexTechTheme.colors.neutral.white,
                            borderRadius: rexTechTheme.borderRadius.large,
                            width: '100%',
                            maxWidth: '800px',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
                        }}>
                            {/* Modal Header */}
                            <div style={{
                                padding: rexTechTheme.spacing.xl,
                                borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: rexTechTheme.spacing.sm
                                }}>
                                    <Plus size={24} color={rexTechTheme.colors.primary.main} />
                                    <h2 style={{
                                        fontSize: rexTechTheme.typography.fontSize.xlarge,
                                        color: rexTechTheme.colors.primary.dark,
                                        fontWeight: rexTechTheme.typography.fontWeight.bold,
                                        margin: 0
                                    }}>
                                        Create New Client
                                    </h2>
                                </div>
                                <button
                                    onClick={() => setShowCreateModal(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: rexTechTheme.colors.neutral.darkGray,
                                        padding: rexTechTheme.spacing.xs,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.2s ease',
                                        ':hover': {
                                            backgroundColor: rexTechTheme.colors.neutral.gray,
                                            color: rexTechTheme.colors.primary.dark
                                        }
                                    }}
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div style={{ padding: rexTechTheme.spacing.xl }}>
                                <form onSubmit={createCompany}>
                                    {errorMessage && (
                                        <div style={{
                                            padding: rexTechTheme.spacing.md,
                                            backgroundColor: rexTechTheme.colors.accent.lightest,
                                            border: `1px solid ${rexTechTheme.colors.accent.orange}`,
                                            borderRadius: rexTechTheme.borderRadius.medium,
                                            color: rexTechTheme.colors.accent.orange,
                                            marginBottom: rexTechTheme.spacing.lg,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: rexTechTheme.spacing.sm
                                        }}>
                                            <AlertCircle size={18} />
                                            {errorMessage}
                                        </div>
                                    )}

                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(2, 1fr)',
                                        gap: rexTechTheme.spacing.lg,
                                        marginBottom: rexTechTheme.spacing.xl
                                    }}>
                                        {/* Company Name */}
                                        {/* <div>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: rexTechTheme.spacing.xs,
                                                color: rexTechTheme.colors.primary.dark,
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                fontSize: rexTechTheme.typography.fontSize.small
                                            }}>
                                                Company Name *
                                            </label>
                                            <input
                                                type="text"
                                                name="companyName"
                                                value={companyForm.companyName}
                                                onChange={handleCompanyInputChange}
                                                placeholder="Enter company name"
                                                style={{
                                                    width: '100%',
                                                    padding: rexTechTheme.spacing.md,
                                                    border: `1px solid ${formErrors.companyName ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                                    borderRadius: rexTechTheme.borderRadius.medium,
                                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none',
                                                    boxSizing: 'border-box',
                                                    ':focus': {
                                                        borderColor: rexTechTheme.colors.primary.main,
                                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                                    }
                                                }}
                                            />
                                            {formErrors.companyName && (
                                                <div style={{
                                                    marginTop: rexTechTheme.spacing.xs,
                                                    color: rexTechTheme.colors.accent.orange,
                                                    fontSize: rexTechTheme.typography.fontSize.xs,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: rexTechTheme.spacing.xs
                                                }}>
                                                    <AlertCircle size={12} />
                                                    {formErrors.companyName}
                                                </div>
                                            )}
                                        </div> */}
 <div>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: rexTechTheme.spacing.xs,
                                                color: rexTechTheme.colors.primary.dark,
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                fontSize: rexTechTheme.typography.fontSize.small
                                            }}>
                                                Name *
                                            </label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={companyForm.name}
                                                onChange={handleCompanyInputChange}
                                                placeholder="Enter  name"
                                                style={{
                                                    width: '100%',
                                                    padding: rexTechTheme.spacing.md,
                                                    border: `1px solid ${formErrors.name ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                                    borderRadius: rexTechTheme.borderRadius.medium,
                                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none',
                                                    boxSizing: 'border-box',
                                                    ':focus': {
                                                        borderColor: rexTechTheme.colors.primary.main,
                                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                                    }
                                                }}
                                            />
                                            {formErrors.name && (
                                                <div style={{
                                                    marginTop: rexTechTheme.spacing.xs,
                                                    color: rexTechTheme.colors.accent.orange,
                                                    fontSize: rexTechTheme.typography.fontSize.xs,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: rexTechTheme.spacing.xs
                                                }}>
                                                    <AlertCircle size={12} />
                                                    {formErrors.name}
                                                </div>
                                            )}
                                        </div>

                                        {/* Email */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: rexTechTheme.spacing.xs,
                                                color: rexTechTheme.colors.primary.dark,
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                fontSize: rexTechTheme.typography.fontSize.small,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: rexTechTheme.spacing.xs
                                            }}>
                                                <Mail size={14} />
                                                Email Address *
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={companyForm.email}
                                                onChange={handleCompanyInputChange}
                                                placeholder="your@example.com"
                                                style={{
                                                    width: '100%',
                                                    padding: rexTechTheme.spacing.md,
                                                    border: `1px solid ${formErrors.email ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                                    borderRadius: rexTechTheme.borderRadius.medium,
                                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none',
                                                    boxSizing: 'border-box',
                                                    ':focus': {
                                                        borderColor: rexTechTheme.colors.primary.main,
                                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                                    }
                                                }}
                                            />
                                            {formErrors.email && (
                                                <div style={{
                                                    marginTop: rexTechTheme.spacing.xs,
                                                    color: rexTechTheme.colors.accent.orange,
                                                    fontSize: rexTechTheme.typography.fontSize.xs,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: rexTechTheme.spacing.xs
                                                }}>
                                                    <AlertCircle size={12} />
                                                    {formErrors.email}
                                                </div>
                                            )}
                                        </div>

                                        {/* Phone Number */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: rexTechTheme.spacing.xs,
                                                color: rexTechTheme.colors.primary.dark,
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                fontSize: rexTechTheme.typography.fontSize.small,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: rexTechTheme.spacing.xs
                                            }}>
                                                <Phone size={14} />
                                                Phone Number *
                                            </label>
                                            <input
                                                type="tel"
                                                name="phoneNumber"
                                                value={companyForm.phoneNumber}
                                                onChange={handleCompanyInputChange}
                                                placeholder="+1 (555) 123-4567"
                                                style={{
                                                    width: '100%',
                                                    padding: rexTechTheme.spacing.md,
                                                    border: `1px solid ${formErrors.phoneNumber ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                                    borderRadius: rexTechTheme.borderRadius.medium,
                                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none',
                                                    boxSizing: 'border-box',
                                                    ':focus': {
                                                        borderColor: rexTechTheme.colors.primary.main,
                                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                                    }
                                                }}
                                            />
                                            {formErrors.phoneNumber && (
                                                <div style={{
                                                    marginTop: rexTechTheme.spacing.xs,
                                                    color: rexTechTheme.colors.accent.orange,
                                                    fontSize: rexTechTheme.typography.fontSize.xs,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: rexTechTheme.spacing.xs
                                                }}>
                                                    <AlertCircle size={12} />
                                                    {formErrors.phoneNumber}
                                                </div>
                                            )}
                                        </div>

                                        {/* Address */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: rexTechTheme.spacing.xs,
                                                color: rexTechTheme.colors.primary.dark,
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                fontSize: rexTechTheme.typography.fontSize.small,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: rexTechTheme.spacing.xs
                                            }}>
                                                <MapPin size={14} />
                                                Address *
                                            </label>
                                            <input
                                                type="text"
                                                name="address"
                                                value={companyForm.address}
                                                onChange={handleCompanyInputChange}
                                                placeholder="123 Main Street"
                                                style={{
                                                    width: '100%',
                                                    padding: rexTechTheme.spacing.md,
                                                    border: `1px solid ${formErrors.address ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                                    borderRadius: rexTechTheme.borderRadius.medium,
                                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none',
                                                    boxSizing: 'border-box',
                                                    ':focus': {
                                                        borderColor: rexTechTheme.colors.primary.main,
                                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                                    }
                                                }}
                                            />
                                            {formErrors.address && (
                                                <div style={{
                                                    marginTop: rexTechTheme.spacing.xs,
                                                    color: rexTechTheme.colors.accent.orange,
                                                    fontSize: rexTechTheme.typography.fontSize.xs,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: rexTechTheme.spacing.xs
                                                }}>
                                                    <AlertCircle size={12} />
                                                    {formErrors.address}
                                                </div>
                                            )}
                                        </div>

                                        {/* City */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: rexTechTheme.spacing.xs,
                                                color: rexTechTheme.colors.primary.dark,
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                fontSize: rexTechTheme.typography.fontSize.small
                                            }}>
                                                City *
                                            </label>
                                            <input
                                                type="text"
                                                name="city"
                                                value={companyForm.city}
                                                onChange={handleCompanyInputChange}
                                                placeholder="New York"
                                                style={{
                                                    width: '100%',
                                                    padding: rexTechTheme.spacing.md,
                                                    border: `1px solid ${formErrors.city ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                                    borderRadius: rexTechTheme.borderRadius.medium,
                                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none',
                                                    boxSizing: 'border-box',
                                                    ':focus': {
                                                        borderColor: rexTechTheme.colors.primary.main,
                                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                                    }
                                                }}
                                            />
                                            {formErrors.city && (
                                                <div style={{
                                                    marginTop: rexTechTheme.spacing.xs,
                                                    color: rexTechTheme.colors.accent.orange,
                                                    fontSize: rexTechTheme.typography.fontSize.xs,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: rexTechTheme.spacing.xs
                                                }}>
                                                    <AlertCircle size={12} />
                                                    {formErrors.city}
                                                </div>
                                            )}
                                        </div>

                                        {/* Country */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: rexTechTheme.spacing.xs,
                                                color: rexTechTheme.colors.primary.dark,
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                fontSize: rexTechTheme.typography.fontSize.small,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: rexTechTheme.spacing.xs
                                            }}>
                                                <Globe size={14} />
                                                Country *
                                            </label>
                                            <input
                                                type="text"
                                                name="country"
                                                value={companyForm.country}
                                                onChange={handleCompanyInputChange}
                                                placeholder="United States"
                                                style={{
                                                    width: '100%',
                                                    padding: rexTechTheme.spacing.md,
                                                    border: `1px solid ${formErrors.country ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                                    borderRadius: rexTechTheme.borderRadius.medium,
                                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none',
                                                    boxSizing: 'border-box',
                                                    ':focus': {
                                                        borderColor: rexTechTheme.colors.primary.main,
                                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                                    }
                                                }}
                                            />
                                            {formErrors.country && (
                                                <div style={{
                                                    marginTop: rexTechTheme.spacing.xs,
                                                    color: rexTechTheme.colors.accent.orange,
                                                    fontSize: rexTechTheme.typography.fontSize.xs,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: rexTechTheme.spacing.xs
                                                }}>
                                                    <AlertCircle size={12} />
                                                    {formErrors.country}
                                                </div>
                                            )}
                                        </div>

                                      

                                        {/* Logo Upload */}
                                        <div style={{ gridColumn: 'span 2' }}>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: rexTechTheme.spacing.xs,
                                                color: rexTechTheme.colors.primary.dark,
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                fontSize: rexTechTheme.typography.fontSize.small,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: rexTechTheme.spacing.xs
                                            }}>
                                                <Upload size={14} />
                                                Company Logo (Optional)
                                            </label>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: rexTechTheme.spacing.lg
                                            }}>
                                                <div>
                                                    <input
                                                        type="file"
                                                        id="logoFile"
                                                        accept="image/*"
                                                        onChange={handleLogoChange}
                                                        style={{ display: 'none' }}
                                                    />
                                                    <label htmlFor="logoFile"
                                                        style={{
                                                            padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                                            backgroundColor: rexTechTheme.colors.primary.lightest,
                                                            border: `1px solid ${rexTechTheme.colors.primary.light}`,
                                                            color: rexTechTheme.colors.primary.main,
                                                            borderRadius: rexTechTheme.borderRadius.medium,
                                                            cursor: 'pointer',
                                                            fontSize: rexTechTheme.typography.fontSize.small,
                                                            fontWeight: rexTechTheme.typography.fontWeight.medium,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: rexTechTheme.spacing.sm,
                                                            transition: 'all 0.2s ease',
                                                            ':hover': {
                                                                backgroundColor: rexTechTheme.colors.primary.light,
                                                                color: rexTechTheme.colors.neutral.white
                                                            }
                                                        }}
                                                    >
                                                        <Upload size={16} />
                                                        Choose File
                                                    </label>
                                                    {logoFile && (
                                                        <div style={{
                                                            marginTop: rexTechTheme.spacing.sm,
                                                            fontSize: rexTechTheme.typography.fontSize.xs,
                                                            color: rexTechTheme.colors.neutral.darkGray
                                                        }}>
                                                            Selected: {logoFile.name}
                                                        </div>
                                                    )}
                                                </div>
                                                {logoPreview && (
                                                    <div style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        borderRadius: rexTechTheme.borderRadius.medium,
                                                        overflow: 'hidden',
                                                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`
                                                    }}>
                                                        <img 
                                                            src={logoPreview} 
                                                            alt="Logo preview" 
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Password Field - Hidden but included */}
                                        <input
                                            type="hidden"
                                            name="userType"
                                            value="CLIENTS"
                                        />
                                        <input
                                            type="hidden"
                                            name="password"
                                            value="Test@123"
                                        />
                                    </div>

                                    {/* Active Status */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: rexTechTheme.spacing.sm,
                                        padding: rexTechTheme.spacing.md,
                                        backgroundColor: rexTechTheme.colors.primary.lightest,
                                        borderRadius: rexTechTheme.borderRadius.medium,
                                        border: `1px solid ${rexTechTheme.colors.primary.lighter}`,
                                        marginBottom: rexTechTheme.spacing.xl
                                    }}>
                                        <input
                                            type="checkbox"
                                            id="isActive"
                                            name="isActive"
                                            checked={companyForm.isActive}
                                            onChange={handleCompanyInputChange}
                                            style={{
                                                width: '18px',
                                                height: '18px',
                                                cursor: 'pointer'
                                            }}
                                        />
                                        <label htmlFor="isActive" style={{
                                            color: rexTechTheme.colors.primary.dark,
                                            fontWeight: rexTechTheme.typography.fontWeight.medium,
                                            cursor: 'pointer',
                                            fontSize: rexTechTheme.typography.fontSize.small
                                        }}>
                                            Client is active and can login to the system
                                        </label>
                                    </div>

                                    {/* Action Buttons */}
                                    <div style={{
                                        display: 'flex',
                                        gap: rexTechTheme.spacing.md,
                                        paddingTop: rexTechTheme.spacing.lg,
                                        borderTop: `1px solid ${rexTechTheme.colors.neutral.gray}`
                                    }}>
                                        <button
                                            type="button"
                                            onClick={() => setShowCreateModal(false)}
                                            style={{
                                                flex: 1,
                                                padding: rexTechTheme.spacing.md,
                                                backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                                border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                                color: rexTechTheme.colors.neutral.darkGray,
                                                borderRadius: rexTechTheme.borderRadius.medium,
                                                fontSize: rexTechTheme.typography.fontSize.regular,
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                ':hover': {
                                                    backgroundColor: rexTechTheme.colors.neutral.gray
                                                }
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isCreating}
                                            style={{
                                                flex: 1,
                                                padding: rexTechTheme.spacing.md,
                                                background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                                                color: rexTechTheme.colors.neutral.white,
                                                border: 'none',
                                                borderRadius: rexTechTheme.borderRadius.medium,
                                                fontSize: rexTechTheme.typography.fontSize.regular,
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                cursor: isCreating ? 'not-allowed' : 'pointer',
                                                transition: 'all 0.3s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: rexTechTheme.spacing.sm,
                                                opacity: isCreating ? 0.7 : 1,
                                                boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                                                ':hover': isCreating ? {} : {
                                                    background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                                                }
                                            }}
                                        >
                                            {isCreating ? (
                                                <>
                                                    <div style={{
                                                        width: '16px',
                                                        height: '16px',
                                                        border: `2px solid ${rexTechTheme.colors.neutral.white}`,
                                                        borderTopColor: 'transparent',
                                                        borderRadius: '50%',
                                                        animation: 'spin 1s linear infinite'
                                                    }} />
                                                    Creating...
                                                </>
                                            ) : (
                                                <>
                                                    <Save size={18} />
                                                    Create Client
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Clients;